import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  NativeSelect,
  TextField,
  Typography,
} from "@mui/material";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import React, { ChangeEvent, FormEvent, useState } from "react";
import EventData from "./EventData";
import fetchBuilder from "fetch-retry-ts";
import OrderResponse from "./OrderResponse";

interface StripePaymentFormProps {
  event: EventData;
  updateNumberOfTicketsCallback: (price: number, noOfTickets: number) => void;
}
const fetchRetry = fetchBuilder(fetch);

function StripePaymentForm(props: StripePaymentFormProps) {
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [totalAmount, setTotalAmount] = useState(props.event.price * 1);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const stripe = useStripe();
  const elements = useElements();

  const validateInputs = () => {
    const email = document.getElementById("email") as HTMLInputElement;
    const firstName = document.getElementById("firstName") as HTMLInputElement;
    const lastName = document.getElementById("lastName") as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      isValid = false;
    } else {
      setEmailError(false);
    }

    if (!firstName.value || firstName.value.length < 1) {
      setFirstNameError(true);
      isValid = false;
    } else {
      setFirstNameError(false);
    }

    if (!lastName.value || lastName.value.length < 1) {
      setLastNameError(true);
      isValid = false;
    } else {
      setLastNameError(false);
    }

    return isValid;
  };

  const createOrder = async (paymentToken: string) => {
    const numberOfTickets = Number.parseInt(
      (document.getElementById("numberOfTickets") as HTMLSelectElement).value,
    );
    return await fetchRetry("/api/order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        eventId: props.event.id,
        firstName: (document.getElementById("firstName") as HTMLInputElement)
          .value,
        lastName: (document.getElementById("lastName") as HTMLInputElement)
          .value,
        emailAddress: (document.getElementById("email") as HTMLInputElement)
          .value,
        numberOfTickets: numberOfTickets,
        paymentToken: paymentToken,
      }),
    });
  };

  const confirmOrder = async (orderId: string) => {
    return await fetchRetry("/api/payment/confirm", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        orderId,
      }),
    });
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (validateInputs() && stripe && elements) {
      setOrderSubmitting(true);

      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        setOrderSubmitting(false);
        return;
      }

      const { error, confirmationToken } = await stripe.createConfirmationToken(
        {
          elements,
          params: {},
        },
      );

      if (error) {
        setErrorMessage(error.message);
        setOrderSubmitting(false);
        return;
      }

      const response = await createOrder(confirmationToken.id);

      if (!response.ok) {
        const responseBody = await response.text();
        setErrorMessage(responseBody);
        setOrderSubmitting(false);
        return;
      }

      const responseJson = await response.json();
      const orderData = responseJson as OrderResponse;

      if (orderData.paymentStatus === "REQUIRES_CONFIRMATION") {
        const { error } = await stripe.handleNextAction({
          clientSecret: orderData.paymentSecret,
        });

        if (error) {
          setErrorMessage(error.message);
          setOrderSubmitting(false);
          return;
        }

        const confirmResponse = await confirmOrder(orderData.orderId);

        if (!confirmResponse.ok) {
          const confirmResponseBody = await confirmResponse.text();
          setErrorMessage(confirmResponseBody);
          setOrderSubmitting(false);
          return;
        }
      }

      setPaymentCompleted(true);
    }
  }

  function numberOfTicketsChange(event: ChangeEvent<HTMLSelectElement>): void {
    const dropdown = event.target as HTMLSelectElement;
    const noOfTickets = Number.parseInt(dropdown.value);
    props.updateNumberOfTicketsCallback(props.event.price, noOfTickets);
    setTotalAmount(noOfTickets * props.event.price);
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {paymentCompleted ? (
        <Alert severity="success">
          Tickets confirmed. You will receive an email confirming your order.
        </Alert>
      ) : (
        <>
          <FormControl>
            <FormLabel htmlFor="numberOfTickets">
              Please select the number of tickets you would like to purchase
            </FormLabel>
            <NativeSelect
              defaultValue={"1"}
              onChange={numberOfTicketsChange}
              inputProps={{
                name: "numberOfTickets",
                id: "numberOfTickets",
              }}
            >
              {Array.from(
                { length: props.event.maxTickets },
                (v, k) => k + 1,
              ).map((value) => (
                <option value={value}>{value}</option>
              ))}
            </NativeSelect>
          </FormControl>
          <Typography component="h4" variant="subtitle2" sx={{ width: "100%" }}>
            {`Total amount to pay - ${new Intl.NumberFormat("en-gb", {
              style: "currency",
              currency: "GBP",
            }).format(totalAmount)}`}
          </Typography>
          <FormControl>
            <FormLabel htmlFor="firstName">First name</FormLabel>
            <TextField
              autoComplete="given-name"
              name="firstName"
              required
              fullWidth
              id="firstName"
              error={firstNameError}
              helperText={
                firstNameError ? "Please enter a valid first name" : ""
              }
              color={firstNameError ? "error" : "primary"}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="lastName">Last name</FormLabel>
            <TextField
              autoComplete="family-name"
              name="lastName"
              required
              fullWidth
              id="lastName"
              error={lastNameError}
              helperText={lastNameError ? "Please enter a valid last name" : ""}
              color={lastNameError ? "error" : "primary"}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="email">Email</FormLabel>
            <TextField
              required
              fullWidth
              id="email"
              placeholder="your@email.com"
              name="email"
              autoComplete="email"
              variant="outlined"
              error={emailError}
              helperText={
                emailError ? "Please enter a valid email address" : ""
              }
              color={emailError ? "error" : "primary"}
            />
          </FormControl>
          <PaymentElement />
          {errorMessage && errorMessage.length > 0 ? (
            <Alert severity="error">{errorMessage}</Alert>
          ) : (
            <></>
          )}
          <Button
            type="submit"
            loading={orderSubmitting}
            loadingPosition="start"
            fullWidth
            variant="contained"
            onClick={validateInputs}
          >
            Buy Tickets
          </Button>
        </>
      )}
    </Box>
  );
}

export default StripePaymentForm;
